import CommentsIcon from "assets/icons/colorful/CommentsIcon";
import EngagementsIcon from "assets/icons/colorful/EngagementsIcon";
import LikesIcon from "assets/icons/colorful/LikesIcon";
import ViewsIcon from "assets/icons/colorful/ViewsIcon";
import CloseButton from "Components/Common/CloseButton";
import InstagramCaption from "Components/Common/InstagramCaption";
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom";
import { Offcanvas, OffcanvasBody } from "reactstrap";
import { fetchCollaboration } from "slices/campaigns/collaborations/thunk";
import { fetchPost, fetchSocialPostThunk } from "slices/influencer/thunk";
import { AppDispatch, RootState } from "store"
import { formatDuration, formatNumber, formatTime } from "utils/helpers";
import userProfilePicture from "../../../assets/images/users/user-dummy-img.jpg";

interface PostPerformanceOffcanvasInterface {
    isOpen: boolean,
    onClose: () => void,
    collaborationId: string | null,
}
const PostPerformanceOffcanvas: React.FC<PostPerformanceOffcanvasInterface> = ({ isOpen, onClose, collaborationId }) => {

    const { campaignId } = useParams();
    const dispatch: AppDispatch = useDispatch();
    const configurations = useSelector((state: RootState) => state.auth.configurations);

    const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

    const collaboration = useSelector((state: RootState) => state.collaborations.campaigns[campaignId!]?.collaborations?.[collaborationId!]?.collaboration);

    const selectedPost = useSelector((state: RootState) => state.influencers.social_posts?.[collaboration?.influencer?.social_account?.uuid]?.[selectedPostId!]?.post);

    const [date, time] = selectedPost?.posted_on
        ? selectedPost.posted_on?.split(", ").reduce((acc, val, i) => (i < 2 ? [acc[0] + (acc[0] ? ", " : "") + val, acc[1]] : [acc[0], val]), ["", ""])
        : ["", ""];

    useEffect(() => {
        if (collaborationId && isOpen && campaignId) {
            dispatch(fetchCollaboration({ collaborationId, campaignId }))
        }
    }, [dispatch, isOpen, collaborationId, campaignId])

    const getPosts = (media_product_type = 'ALL') => {
        if (!collaboration || !collaboration.posts || !collaboration.posts.length) return [];
        if (media_product_type === 'ALL') return collaboration.posts;
        return collaboration.posts.filter((post) => post.social_post?.type === media_product_type && post.status === 'approved');
    }

    useEffect(() => {
        if (collaboration && collaboration.posts && collaboration.posts.length > 0) {
            setSelectedPostId(collaboration.posts[0].social_post.uuid);
        }
    }, [collaboration])

    useEffect(() => {
        if (selectedPostId && collaboration?.influencer?.social_account) {
            dispatch(fetchSocialPostThunk({ socialAccountId: collaboration.influencer.social_account.uuid, postId: selectedPostId }));
        }
    }, [selectedPostId, collaboration?.influencer.uuid])

    useEffect(() => {
        if (!isOpen)
            setSelectedPostId(null);
    }, [isOpen])

    return (
        <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" id="post-performance-offcanvas">
            <CloseButton onClose={onClose} />
            <OffcanvasBody className="p-0">
                {
                    collaboration &&
                    <React.Fragment>
                        <div className="post-performance-header d-flex justify-content-between align-items-center">
                            <div className="influencer-profile d-flex gap-2 align-items-center">
                                <img src={collaboration.influencer?.social_account?.profile_picture_url ?? userProfilePicture} />
                                <span>{collaboration.influencer.name}</span>
                            </div>
                            <div className="d-flex justify-content-end align-items-center gap-2">
                                <span className="badge">{collaboration.influencer.gender_name}</span>
                                <span className="badge">{collaboration.influencer.age} Yrs</span>
                                {
                                    collaboration.platform === 'ig' &&
                                    <>
                                        <span className="badge">Story Posted: {getPosts('STORY').length}</span>
                                        <span className="badge">Post Posted: {getPosts('FEED').length}</span>
                                        <span className="badge">Reel Posted: {getPosts('REELS').length}</span>
                                    </>
                                }
                                {
                                    collaboration.platform === 'yt' &&
                                    <span className="badge">Video Posted: {getPosts('VIDEO').length}</span>
                                }

                            </div>
                        </div>
                        <div className="post-performance-content d-flex justify-content-start">
                            <div className="thumbnails d-flex flex-column align-items-center gap-2">
                                {
                                    collaboration.posts.map((post, i) => {
                                        if (post.status !== 'approved') return null;
                                        return (
                                            <div className={`post-thumbnail-wrapper ${(selectedPostId === post.social_post.uuid) && 'active'}`}>
                                                <span style={{ backgroundImage: `url(${post.social_post.thumbnail_url})` }} onClick={() => setSelectedPostId(post.social_post.uuid)} className={`cursor-pointer ${(selectedPostId === post.social_post.uuid) && 'active'} post-thumbnail-sm`} key={i}>
                                                </span>
                                            </div>
                                        )
                                    })
                                }
                            </div>
                            <div className="post-media">
                                {
                                    selectedPost?.platform === 'ig' && selectedPost?.ig &&
                                    <>
                                        {
                                            ['IMAGE', 'CAROUSEL_ALBUM'].includes(selectedPost.ig.media_type!) &&
                                            <img src={selectedPost.ig.media_url} className="post-performance-post" />
                                        }
                                        {
                                            selectedPost.ig.media_type === 'VIDEO' &&
                                            <video src={selectedPost.ig.media_url} className="post-performance-post" controls />
                                        }
                                    </>
                                }
                                {
                                    selectedPost?.platform === 'yt' && selectedPost?.yt &&
                                    <iframe
                                        src={selectedPost.yt.permalink ? `https://www.youtube.com/embed/${selectedPost.yt.permalink.split('/').pop()?.split('?')[0]}` : ""}
                                        className="post-performance-post"
                                        style={{ objectFit: "contain" }}
                                        frameBorder="0"
                                        allowFullScreen
                                        title="YouTube video"
                                    ></iframe>
                                }
                            </div>
                            <div className="post-performance-caption">
                                <div className="d-flex gap-2">
                                    <span className="icon-space">
                                        <i className="ri-calendar-2-line text-primary"></i>
                                    </span>
                                    <div className="d-flex flex-column post-timing">
                                        <span>{date}</span>
                                        <span>@{time}</span>
                                    </div>
                                </div>
                                <div className="post-performance-caption-text my-3">
                                    <InstagramCaption text={selectedPost?.platform === 'ig' ? selectedPost?.ig?.caption : selectedPost?.platform === 'yt' ? selectedPost?.yt?.title : ''} />
                                </div>
                            </div>
                            <div className="post-performance-stats">
                                <div className="row g-2">
                                    <div className="col-xl-6 col-md-12">
                                        <div className="inf-stat-card bg-pink-light">
                                            <LikesIcon />
                                            <span className="inf-stat-card-value px-2">{formatNumber(selectedPost?.like_count)}</span>
                                            <span className="inf-stat-card-label">Likes</span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-md-12">
                                        <div className="inf-stat-card bg-yellow-light">
                                            <CommentsIcon />
                                            <span className="inf-stat-card-value px-2">{formatNumber(selectedPost?.comments_count)}</span>
                                            <span className="inf-stat-card-label">Comments</span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-md-12">
                                        <div className="inf-stat-card bg-purple-light">
                                            <ViewsIcon />
                                            <span className="inf-stat-card-value px-2">{formatNumber(selectedPost?.views_count!)}</span>
                                            <span className="inf-stat-card-label">Views</span>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-md-12">
                                        <div className="inf-stat-card bg-orange-light">
                                            <EngagementsIcon />
                                            <span className={`inf-stat-card-value px-2`}>{selectedPost?.engagement_score}</span>
                                            <span className="inf-stat-card-label">Engagement</span>
                                        </div>
                                    </div>
                                    {
                                        collaboration?.platform === 'ig' &&
                                        <>
                                            <div className="col-xl-6 col-md-12">
                                                <div className="inf-stat-card bg-secondary-subtle">
                                                    <span className="stat-icon bg-secondary">
                                                        <i className="mdi mdi-share-variant text-white fs-5"></i>
                                                    </span>
                                                    <span className={`inf-stat-card-value px-2`}>{selectedPost?.ig?.shares_count ?? 0}</span>
                                                    <span className="inf-stat-card-label">Shares</span>
                                                </div>
                                            </div>
                                            {
                                                selectedPost?.ig?.total_watch_time !== null &&
                                                <div className="col-xl-6 col-md-12">
                                                    <div className="inf-stat-card bg-green-light">
                                                        <span className="stat-icon bg-green">
                                                            <i className="mdi mdi-clock-time-eight-outline text-white fs-5"></i>
                                                        </span>
                                                        <span className="inf-stat-card-value px-2">{formatDuration(selectedPost?.ig?.total_watch_time ?? 0)}</span>
                                                        <span className="inf-stat-card-label">Total Watch Time</span>
                                                    </div>
                                                </div>
                                            }
                                            {
                                                configurations?.agency_show_reach_insights === 'y' &&
                                                <div className="col-xl-6 col-md-12">
                                                    <div className="inf-stat-card bg-primary-subtle">
                                                        <span className="stat-icon bg-primary">
                                                            <i className="mdi mdi-account-group text-white fs-5"></i>
                                                        </span>
                                                        <span className="inf-stat-card-value px-2">{formatNumber(selectedPost?.ig?.reach_count ?? 0)}</span>
                                                        <span className="inf-stat-card-label">Accounts Reached</span>
                                                    </div>
                                                </div>
                                            }
                                            {
                                                configurations?.agency_show_avg_watch_time_insights === 'y' &&
                                                <div className="col-xl-6 col-md-12">
                                                    <div className="inf-stat-card bg-danger-subtle">
                                                        <span className="stat-icon bg-danger">
                                                            <i className="mdi mdi-clock-time-eight-outline text-white fs-5"></i>
                                                        </span>
                                                        <span className="inf-stat-card-value px-2">{formatDuration(selectedPost?.ig?.avg_watch_time ?? 0)}</span>
                                                        <span className="inf-stat-card-label">Avg. Watch Time</span>
                                                    </div>
                                                </div>
                                            }
                                        </>
                                    }
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                }
            </OffcanvasBody>
        </Offcanvas>
    )
}

export default PostPerformanceOffcanvas 